import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from "@/motion/appMotion";
import FaqAccordionPanel from "../../common/FaqAccordionPanel";
import { faqContent } from '../../../data/landingPage/faqContent';
import useLandingMotion from '../../../hooks/useLandingMotion';
import usePublicFaqs from '../../../hooks/usePublicFaqs';
import {
  LANDING_CONTAINER_PADDED,
  LANDING_INNER,
} from '../../../constants/landingPageLayout';

const FAQ = ({ unifiedShade = false, appSurface = false }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const { landingFaqs } = usePublicFaqs();
  const { getProps, getInViewVariants } = useLandingMotion();

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <section
      id="faq"
      className="relative w-full bg-[#F1F5F980] overflow-hidden"
    >
      <div
        className={`${LANDING_CONTAINER_PADDED} flex flex-col gap-[42px] py-10 sm:py-12 lg:py-[39px]`}
      >
        <div className={`${LANDING_INNER} flex flex-col gap-[42px]`}>
        
        {/* Heading */}
        <motion.div
          {...getProps({ duration: 0.6 })}
          className="w-full text-center flex flex-col gap-2"
        >
          <h2 className="text-[36px] lg:text-[40px] font-semibold text-[#0F172A] tracking-tight" style={{ fontFamily: '"Rethink Sans", sans-serif' }}>
            We've answered all your questions
          </h2>
          <p className="text-[18px] text-[#64748B]" style={{ fontFamily: '"Inter", sans-serif' }}>
            Everything you need to know about our job fetching platform
          </p>
        </motion.div>

        {/* Accordion List */}
        <motion.div
          {...getInViewVariants(containerVariants)}
          className="w-full flex flex-col"
        >
          {landingFaqs.map((faq, index) => {
            const isOpen = openIndex === index;
            // Only show a top border on the first item to match the clean list look, or bottom borders on all.
            // The image shows bottom borders on inactive items, and a block style. Let's do border-b on all.
            return (
              <motion.div
                key={faq.id ?? index}
                variants={itemVariants}
                className={`w-full border-b border-[#E2E8F0] transition-colors duration-300 ${
                  isOpen ? 'bg-[#F8FAFC]' : 'bg-transparent hover:bg-slate-50/50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={isOpen}
                  className="w-full flex justify-between items-center py-5 px-4 text-left focus:outline-none"
                >
                  <span
                    className={`font-semibold text-[17px] leading-snug pr-4 transition-colors ${
                      isOpen ? 'text-[#6366F1]' : 'text-[#0F172A]'
                    }`}
                    style={{ fontFamily: '"Rethink Sans", sans-serif' }}
                  >
                    {faq.question}
                  </span>
                  
                  {/* Plus/Minus Icon Box */}
                  <div
                    className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[4px] text-white transition-colors duration-300 ${
                      isOpen ? 'bg-[#0F172A]' : 'bg-[#CBD5E1]'
                    }`}
                    aria-hidden
                  >
                    {isOpen ? (
                      <svg
                        className="w-[14px] h-[14px]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    ) : (
                      <svg
                        className="w-[14px] h-[14px]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    )}
                  </div>
                </button>

                <FaqAccordionPanel
                  isOpen={isOpen}
                  contentClassName="w-full px-4 pb-6 pt-1 font-normal text-[15px] leading-relaxed text-[#64748B]"
                >
                  <p className="w-full max-w-[85%] whitespace-pre-line text-left" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {faq.answer}
                  </p>
                </FaqAccordionPanel>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA Block */}
        <motion.div
          {...getProps({ variant: 'fade', delay: 0.2, duration: 0.6 })}
          className="w-full flex flex-col md:flex-row items-center justify-between gap-6 border border-solid border-[#D4D9DF] rounded-[24px] bg-[#4F46E50D] py-8 px-8 md:px-10"
        >
          <h3 className="text-[26px] md:text-[28px] font-semibold text-[#0F172A]" style={{ fontFamily: '"Rethink Sans", sans-serif' }}>
            Still have questions?
          </h3>
          
          <Link
            to="/contact"
            className="group inline-flex shrink-0 items-center gap-0"
          >
            <span
              className="inline-flex h-[39.52px] items-center justify-center whitespace-nowrap rounded-[17.89px] bg-[#4F46E5] px-[10.44px] text-[13.42px] font-medium leading-none tracking-normal text-white shadow-md transition-colors duration-200 group-hover:bg-[#4338CA]"
              style={{ fontFamily: '"Inter", sans-serif' }}
            >
              Contact our support team
            </span>
            <span className="inline-flex h-[39.52px] w-[39.52px] shrink-0 items-center justify-center rounded-full bg-[#4F46E5] p-[7.46px] text-white shadow-md transition-colors duration-200 group-hover:bg-[#4338CA]">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </span>
          </Link>
        </motion.div>

        </div>
      </div>
    </section>
  );
};

export default FAQ;
